import { Link } from "react-router-dom";
import { ArrowLeft, Globe2, UserCheck, ShieldCheck, Coins, XCircle, Scale, Mail } from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";

const Section = ({ icon: Icon, n, title, testid, children }) => (
  <section data-testid={testid} className="border-t border-neutral-200 py-10 first:border-t-0 first:pt-0">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85]">
        <Icon className="h-5 w-5" />
      </div>
      <h2 className="text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
        {n}. {title}
      </h2>
    </div>
    <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-neutral-700 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 [&_strong]:text-neutral-900 [&_a]:font-semibold [&_a]:text-[#0A8B85] [&_a]:underline-offset-4 hover:[&_a]:underline">
      {children}
    </div>
  </section>
);

export default function TermsOfService() {
  return (
    <div data-testid="terms-page" className="min-h-screen bg-white text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      <Navbar />

      <header className="bg-[#04302E] text-white">
        <div className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
          <Link to="/" data-testid="terms-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 transition-colors hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.3em] text-[#7BD3CE]">
            Legal · Unlock Your Capital Potential
          </p>
          <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">Terms of Service</h1>
          <p className="mt-4 max-w-2xl text-base text-white/70">
            By using the Clinq platform you agree to these terms. If you do not agree,
            please do not use Clinq.
          </p>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-white/40">
            Last updated: June 2026
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14 lg:py-16">
        <Section icon={Globe2} n={1} title="The Clinq Platform" testid="tos-platform">
          <p>
            Clinq is a technology platform that connects African SMEs with funders. We are{" "}
            <strong>not</strong> a broker-dealer, do not provide investment, legal, or tax
            advice, and are not a party to any transaction between a business and a funder.
          </p>
        </Section>

        <Section icon={UserCheck} n={2} title="Eligibility" testid="tos-eligibility">
          <p>
            You must have authority to bind the business or fund you sign up on behalf of,
            and to upload the information you provide.
          </p>
        </Section>

        <Section icon={ShieldCheck} n={3} title="Acceptable Use" testid="tos-acceptable-use">
          <ul>
            <li>No misrepresentation of your business, fund, or identity.</li>
            <li>No scraping, redistribution, or off-platform circumvention.</li>
            <li>No upload of content you do not have rights to.</li>
          </ul>
        </Section>

        <Section icon={Coins} n={4} title="Fees" testid="tos-fees">
          <p>
            Founders may use the platform&apos;s core onboarding and matching at no cost.
            Optional paid services — including the detailed AI readiness report (CCR
            Score&trade; unlock) and add-on services — are clearly priced before purchase.
            Funder plans, where applicable, are presented before any charge.
          </p>
        </Section>

        <Section icon={XCircle} n={5} title="Termination" testid="tos-termination">
          <p>
            We may suspend or terminate any account that violates these terms or our{" "}
            <Link to="/nda">NDA &amp; Non-Circumvent Agreement</Link>.
          </p>
        </Section>

        <Section icon={Scale} n={6} title="Governing Law" testid="tos-law">
          <p>
            These terms are governed by the laws of the Republic of Kenya. Disputes go to
            mediation in Nairobi and then arbitration under NCIA Rules.
          </p>
        </Section>

        <Section icon={Mail} n={7} title="Contact" testid="tos-contact">
          <p>
            Questions about these terms? We&apos;re happy to help.
          </p>
          <div className="rounded-2xl border border-neutral-200 bg-[#F4F8F8] p-6">
            <p className="text-sm font-bold uppercase tracking-wider text-neutral-500">The Clinq Team</p>
            <a href="mailto:hello@clinq.africa" data-testid="tos-contact-email"
              className="mt-2 inline-block text-lg font-bold text-[#0A8B85]">
              hello@clinq.africa
            </a>
            <p className="mt-2 text-sm text-neutral-600">
              Clinq · SNDBX International, CMS House, 850 Chania Ave, Hurlingham, Nairobi, Kenya
            </p>
          </div>
        </Section>
      </main>

      <Footer />
    </div>
  );
}
